import {Injectable} from "@angular/core";
import * as Three from "three";
import {PlaneGeometry, Vector2, Vector3} from "three";
import * as PIXI from "pixi.js";
import {Container} from "pixi.js";
import {Surface} from "./primitives/surface";
import {ThreeService} from "../rendering/threejs/three.service";
import {ThreeInputService} from "../inputs/input-middlewares/three-input.middleware";
import {Rect, RectConstructor} from "../primitives/rect";
import {Store} from "@ngrx/store";
import {selectSurfaceDebug} from "src/app/state/world/world.selector";
import {worldActions} from "src/app/state/world/world.actions";
import {DebugRectPixi} from "../rendering/pixi/drawables/debug-rect.pixi";
import {generateUUID} from "three/src/math/MathUtils.js";
import {selectCameraTarget, selectCameraZoomTarget} from "../../state/camera/camera.selector";
import {auditTime} from "rxjs";
import {CanvasService, CanvasType} from "./canvas/canvas.service";
import {PixiZDepthLayers} from "./canvas/pixi-z-depth-layers.enum";

let DrawableCallback: (
  stage: PIXI.Container,
  timeDelta: number,
  surface: Surface,
) => void;

@Injectable({
  providedIn: "root",
})
export class SurfaceService {
  __surfaces: Map<string, Surface> = new Map();
  // __allocationPadding = 10;
  __debug = true;
  __threeInputService: ThreeInputService;
  __canvasService: CanvasService
  __canvasRect: Rect

  lastCameraPos: Vector3 = new Vector3(0,0,0)

  constructor(
    public three: ThreeService,
    private threeInputService: ThreeInputService,
    private canvasService: CanvasService,
    private store: Store,
  ) {
    this.__canvasRect = new Rect({
      left:0,
      top:0,
      width:2500,
      height:2500,
    })
    this.__threeInputService = threeInputService;
    this.__canvasService = this.canvasService;
    this.store.select(selectSurfaceDebug).subscribe((enabled: boolean) => {
      this.__debug = enabled;
    });
    this.store.select(selectCameraTarget).pipe(
        auditTime(8),
    ).subscribe(async (target) => {
        await this.updateCamera();
    })
    this.store.select(selectCameraZoomTarget).pipe(
        auditTime(8),
    ).subscribe(async (target) => {
      await this.updateCamera();
    })
  }

  // async createMicro(init?: typeof DrawableCallback) {
  //   return this.create({ left: 0, top: 0, width: 100, height: 100 }, init);
  // }
  // async createSmall(init?: typeof DrawableCallback) {
  //   return this.create({ left: 0, top: 0, width: 250, height: 250 }, init);
  // }
  // async createMed(init?: typeof DrawableCallback) {
  //   return this.create({ left: 0, top: 0, width: 500, height: 500 }, init);
  // }
  // async createLarge(init?: typeof DrawableCallback) {
  //   return this.create({ left: 0, top: 0, width: 1000, height: 1000 }, init);
  // }

  async create(rectP: RectConstructor, init?: typeof DrawableCallback) {
    const rect = new Rect(rectP);
    const surfaceId= "surface-" + (rectP.id || generateUUID())
    const surf = new Surface({...rectP, id: surfaceId});
    surf.__debug = this.__debug;
    surf.__surfaceService = this;
    surf.stage = new Container({
      position: {
        x: rect.x,
        y: rect.y
      },
    });
    surf.stage.zIndex = PixiZDepthLayers.Surfaces
    surf.textureIsland = new Rect({...rectP})
    surf.stage.label = surfaceId;
    this.__setupSurfaceMaterial(surf);
    this.threeInputService.addTouchPrimitiveOwnerToMeshId(surf.mesh.uuid, surf);
    surf.rect.__translateParentCoordToLocalCoord = (pos: Vector2) => {
      return this.uvCoordToTextureIslandCoord(pos, surf);
    };
    this.__surfaces.set(surf.id, surf);
    if (init) {
      await init(surf.stage, 1, surf);
    }
    this.three.addToScene(surf.mesh);
    if (this.__debug) {
      this.__debugSurfaceStageBackground(surf);
    }
    this.canvasService
      .getCanvasStage(CanvasType.PostsCanvas)
      .addChild(surf.stage);

    this.lastCameraPos = this.three.camera.position;
    this.updateSurfaceTextureIsland(surf);
    return surf;
  }

  uvCoordToTextureIslandCoord(uvCoord: Vector2, surface: Surface): Vector2 {
    const r = this.transformRectFromPixiSpaceToThreeSpace(
        surface.textureIsland,
    );
    const adjustedSurfaceDimensions = new Vector2(
        surface.rect.width,
        surface.rect.height
    )
    const normalizedLeft = (uvCoord.x - r.left) / r.width
    const normalizedTop = (uvCoord.y - r.top) / r.height
    const surfaceVector = new Vector2(
        normalizedLeft * adjustedSurfaceDimensions.x,
        (1 - normalizedTop) * adjustedSurfaceDimensions.y
    )

    // console.log("coord: ", uvCoord)
    // console.log("pixiSpace: ", normalizedLeft, " ", normalizedTop)
    // console.log("surfaceSpace: ", surfaceVector)
    // console.log("surface: ", surface.width, " ", surface.height)
    return surfaceVector
    //
    //
    //
    // const rTextureIsland = surface.textureIsland;
    // const rNormalised = new Vector2(
    //     surface.textureIsland.width,
    //     surface.textureIsland.height,
    // );
    // const rUV = new Rect({
    //   left: rTextureIsland.left / rNormalised.x,
    //   top: 1 - rTextureIsland.top / rNormalised.y,
    //   right: rTextureIsland.right / rNormalised.x,
    //   bottom: 1 - rTextureIsland.bottom / rNormalised.y,
    // });
    // const result = new Vector2(
    //     ((uvCoord.x - rUV.x) / rUV.width) * rTextureIsland.width,
    //     ((uvCoord.y - rUV.y) / rUV.height) * rTextureIsland.height,
    // );
    // console.log(uvCoord)
    // console.log(result)
    // return result;
  }

  __debugSurfaceStageBackground(surface: Surface) {
    const drp = new DebugRectPixi({
      left: 0,
      top: 0,
      right: surface.rect.width,
      bottom: surface.rect.height,
      roundness: 10,
      colour: [Math.random(), Math.random(), Math.random(), 0.5],
    });
    surface.stage.addChild(drp.sprite);
    drp.sprite.zIndex = 100;
  }

  __setupSurfaceMaterial(surface: Surface) {
    surface.__texture = this.canvasService.getCanvasTexture(
        CanvasType.PostsCanvas
    );
    surface.__material = new Three.MeshBasicMaterial({
      map: surface.__texture,
      transparent: true,
    });
    surface.__material.shadowSide = Three.DoubleSide;
    let geometrySize = new Vector2(
        surface.rect.width * this.pixiToThreeScale(),
        surface.rect.height * this.pixiToThreeScale(),
    );
    surface.__geometry = new Three.PlaneGeometry(
      geometrySize.x,
      geometrySize.y,
    );
    // this.updateMeshUVs(surface);
    this.updateMeshUVs2(surface);
    surface.__mesh = new Three.Mesh(surface.__geometry, surface.__material);
    surface.__mesh.position.set(surface.rect.left, surface.rect.top, 0);
    surface.__mesh.castShadow = true; //default is false
    surface.__mesh.receiveShadow = false; //default
  }

  pixiToThreeScale() {
      return 1.5 / 200
  }

  updateMeshUVs2(surf: Surface) {
    const uvAttribute = surf.__geometry.getAttribute('uv') as Three.BufferAttribute;
    const r = this.transformRectFromPixiSpaceToThreeSpace(
        surf.textureIsland,
    );
    uvAttribute.setXY(0, r.left,  r.bottom);
    uvAttribute.setXY(1, r.right, r.bottom);
    uvAttribute.setXY(2, r.left,  r.top);
    uvAttribute.setXY(3, r.right, r.top);
    uvAttribute.needsUpdate = true;
  }


  // Performance optimisation
  textureIslandResult = Rect.FromLTWH(0, 0, 0, 0)
  transformRectFromPixiSpaceToThreeSpace(rect: Rect): Rect {
    const x = this.__canvasRect.width
    const y = this.__canvasRect.height
    this.textureIslandResult.left = rect.left / x;
    this.textureIslandResult.top = 1 - rect.bottom / y;
    this.textureIslandResult.right = rect.right / x;
    this.textureIslandResult.bottom = 1 - rect.top / y;
    return this.textureIslandResult
  }

  async setup(canvasWidth, canvasHeight) {
    this.store.dispatch(worldActions.surfacesReady({ viewId: "main" }));
    this.__canvasRect.width = canvasWidth
    this.__canvasRect.height = canvasHeight
  }

  async updateCamera() {
    if (!this.three.camera) return;
    this.lastCameraPos = this.three.camera.position;
    for (const surf of this.__surfaces.values()) {
      this.updateSurfaceTextureIsland(surf);
      if (surf.__debug) {
        surf.debugTouchHandlers()
      }
    }
  }

  tmpWorldPosition = new Vector3()

  updateSurfaceTextureIsland(surface: Surface) {
    // PlaneGeometry is center-anchored, so mesh position is the center
    // Calculate top-left corner position
    // const halfWidth = (surface.rect.width * this.pixiToThreeScale()) / 2;
    // const halfHeight = (surface.rect.height * this.pixiToThreeScale()) / 2;
    const width = (surface.mesh.geometry as PlaneGeometry).parameters.width;
    const height = (surface.mesh.geometry as PlaneGeometry).parameters.height;
    this.tmpWorldPosition.set(
      surface.mesh.position.x - width / 2,
      surface.mesh.position.y + (height / 2),
      surface.mesh.position.z
    );
    this.result =  this.getPixiTransform({
      worldPosition: this.tmpWorldPosition,
      camera: this.three.camera,
      width: surface.rect.width * this.pixiToThreeScale(), // Not sure where this modifier comes from
      height: surface.rect.height * this.pixiToThreeScale(),
      spriteWidth: surface.rect.width,
      spriteHeight: surface.rect.height,
      threeWidth: this.three.canvasWidth,
      threeHeight: this.three.canvasHeight,
      pixiWidth: this.__canvasRect.width,
      pixiHeight: this.__canvasRect.height,
    })
    const scale = this.getPixiScaleForThreeObject(
        {
          worldPos: this.tmpWorldPosition,
          threeWorldWidth: surface.width * this.pixiToThreeScale(),
          threeWorldHeight: surface.height * this.pixiToThreeScale(),
          pixiSpriteWidth: surface.width,
          pixiSpriteHeight: surface.height,
          pixiCanvasWidth: this.__canvasRect.width,
          pixiCanvasHeight: this.__canvasRect.height,
        }
    )
    surface.stage.position.x = this.result.x;
    surface.stage.position.y = this.result.y
    surface.stage.scale.x = scale.width
    surface.stage.scale.y = scale.height
    surface.textureIsland.left = this.result.x;
    surface.textureIsland.top = this.result.y;
    surface.textureIsland.right = this.result.x + surface.rect.width * scale.width;
    surface.textureIsland.bottom = this.result.y + surface.rect.height * scale.height;
    this.updateMeshUVs2(surface);
  }

  async render(timeDelta: number) {
  }

  // In Three.js, get screen position of a world point:
  result: any
  screenPosition
  centerNDC
  offsetNDC
  pixelsPerWorldUnit
  tmpVec = new Vector3()
  tmpCenterNDC = new Vector3(0,0,0)
  tmpOffsetNDC = new Vector3(1,0,0)


  getPixiTransform(plane: {
    worldPosition: Vector3;
    camera: Three.Camera;
    width: number;
    height: number;
    spriteWidth: number;
    spriteHeight: number;
    threeWidth: number,
    threeHeight: number,
    pixiWidth: number,
    pixiHeight: number,
  }) {
    // Planes are fixed to XY, so Z = 0
    this.tmpOffsetNDC.set(1,0,0)
    this.tmpCenterNDC.set(0,0,0)
    // this.worldPos3D = new Three.Vector3(plane.worldPosition.x, plane.worldPosition.y, 0);

    // Project world position → NDC [-1, 1]
    this.tmpVec.set(plane.worldPosition.x, plane.worldPosition.y, plane.worldPosition.z);
    this.screenPosition = this.tmpVec.project(plane.camera);

    // NDC → screen pixels (Y axis flipped: Three.js up = +Y, Pixi down = +Y)
    // screenX = (this.screenPosition.x) * plane.pixiWidth;
    // screenY = ((1 - this.screenPosition.y)) * plane.pixiHeight;

    screenX = (((this.screenPosition.x + 1) / 2)) * plane.pixiWidth;
    screenY = ((1 - ((this.screenPosition.y + 1) / 2))) * plane.pixiHeight;

    // Scale: project two points 1 world unit apart at Z=0 to find pixels-per-world-unit
    this.centerNDC = this.tmpCenterNDC.project(plane.camera);
    this.offsetNDC = this.tmpOffsetNDC.project(plane.camera);
    this.pixelsPerWorldUnit = ((this.offsetNDC.x - this.centerNDC.x) / 2) * plane.threeWidth;

    return {
      x: screenX,
      y: screenY,
      scaleX: (plane.width * this.pixelsPerWorldUnit) / plane.spriteWidth,
      scaleY: (plane.height * this.pixelsPerWorldUnit) / plane.spriteHeight,
    };
  }

  translateCameraToPixi(pos, zAxis) {
    const result = -pos * zAxis
    return result
  }

  /**
   * Returns the scale multiplier to apply to a Pixi sprite so it matches
   * the rendered size of a Three.js object at a given world position.
   * Uses camera frustum calculations based on FOV and distance.
   *
   * @param worldPos - The world position of the Three.js object
   * @param threeWorldWidth - The width of the Three.js geometry in world units
   * @param pixiSpriteWidth - The native width of the Pixi sprite in pixels
   * @param pixiCanvasWidth - The width of the Pixi canvas (default 2500)
   * @returns Scale factor to apply to the Pixi sprite
   */
  fovRadians
  getPixiScaleForThreeObject(
      {
        worldPos,
        threeWorldWidth,
        threeWorldHeight,
        pixiSpriteWidth,
        pixiSpriteHeight,
        pixiCanvasWidth,
        pixiCanvasHeight
      }: {
        worldPos: Vector3,
        threeWorldWidth: number,
        threeWorldHeight: number,
        pixiSpriteWidth: number,
        pixiSpriteHeight: number,
        pixiCanvasWidth?: number,
        pixiCanvasHeight?: number
      },
  ) {
    if (!this.fovRadians) this.fovRadians = Three.MathUtils.degToRad(this.three.camera.fov)

    const distance = this.three.camera.position.z - worldPos.z;

    // Calculate frustum dimensions at the object's depth
    const frustumHeight = 2 * distance * Math.tan(this.fovRadians / 2);
    const frustumWidth = frustumHeight * this.three.camera.aspect;

    // Calculate how many Three.js canvas pixels per world unit at this depth
    const pixelsPerWorldUnitX = this.three.canvasWidth / frustumWidth;
    const threeCanvasPixelsX = threeWorldWidth * pixelsPerWorldUnitX;
    const pixiCanvasPixelsX = (threeCanvasPixelsX / this.three.canvasWidth) * pixiCanvasWidth;

    const pixelsPerWorldUnitY = this.three.canvasHeight / frustumHeight;
    const threeCanvasPixelsY = threeWorldHeight * pixelsPerWorldUnitY
    const pixiCanvasPixelsY = (threeCanvasPixelsY / this.three.canvasHeight) * pixiCanvasHeight;

    return {
      width: pixiCanvasPixelsX / pixiSpriteWidth,
      height: pixiCanvasPixelsY / pixiSpriteHeight
    }
  }
}
